/**
 * Description: Capability form component supporting both full tabbed interface and inline quick-create mode
 */

import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Capability } from '@services/api'
import CapabilitySystemsCatalog from '@components/CapabilitySystemsCatalog'
import CapabilityDetailsFields from '@components/CapabilityDetailsFields'

interface CapabilityFormProps {
  capability?: Capability
  onSubmit: (data: Capability) => void
  onCancel: () => void
  isLoading?: boolean
  inline?: boolean
  systemId?: number
  autoLink?: boolean
  onCapabilityCreated?: () => void
}

const CapabilityForm: React.FC<CapabilityFormProps> = ({
  capability,
  onSubmit,
  onCancel,
  isLoading = false,
  inline = false,
  systemId,
  autoLink = true,
  onCapabilityCreated,
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'systems'>('details')
  const [selectedSystemIds, setSelectedSystemIds] = useState<number[]>([])
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Capability>({
    mode: 'onChange',
    defaultValues: capability || { status: 'active' },
  })

  useEffect(() => {
    if (capability) {
      reset(capability)
    }
  }, [capability, reset])

  const handleFormSubmit = async (data: Capability) => {
    const capabilityData = {
      ...data,
      systemIds: !capability?.id ? selectedSystemIds : undefined,
    }

    await onSubmit(capabilityData)

    if (inline && onCapabilityCreated) {
      reset({ status: 'active' })
      setSelectedSystemIds([])
      onCapabilityCreated()
    }
  }

  if (inline) {
    return (
      <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-white/5">
        <CapabilityDetailsFields register={register} errors={errors} compact />
        <div className="flex gap-2">
          <button type="button" onClick={handleSubmit(handleFormSubmit)} className="btn-primary" disabled={isLoading}>
            {systemId && autoLink ? 'Create & Link' : 'Create Capability'}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={isLoading}>
              Cancel
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="overflow-x-auto border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-8" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
              activeTab === 'details'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'cursor-pointer border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            } `}
          >
            Capability Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('systems')}
            className={`border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
              activeTab === 'systems'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'cursor-pointer border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            } `}
          >
            Systems
          </button>
        </nav>
      </div>

      {activeTab === 'details' && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <CapabilityDetailsFields register={register} errors={errors} />
        </div>
      )}

      {activeTab === 'systems' && (
        <div className="py-4">
          <CapabilitySystemsCatalog
            capabilityId={capability?.id}
            capabilityName={capability?.capability_name || 'New Capability'}
            onSystemsChange={!capability?.id ? setSelectedSystemIds : undefined}
            selectedSystemIds={selectedSystemIds}
          />
        </div>
      )}

      {(activeTab === 'details' || !capability) && (
        <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6 dark:border-gray-700">
          <button type="button" onClick={onCancel} className="btn-secondary cursor-pointer" disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" className="btn-primary cursor-pointer" disabled={isLoading}>
            {isLoading ? 'Saving...' : capability?.id ? 'Update Capability' : 'Create Capability'}
          </button>
        </div>
      )}
    </form>
  )
}

export default CapabilityForm
